import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Loader2, Lock, ShieldCheck } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import api, { formatApiError } from '../api';
import { useAuth } from '../AuthContext';
import { useNoIndex } from '../ProtectedRoute';

const roleTarget = (role) =>
  role === 'admin' ? '/admin' : role === 'technicien' ? '/technicien' : '/espace-client';

const LoginPage = () => {
  useNoIndex();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/login', { email, password });
      login(data.token, data.user);
      navigate(roleTarget(data.user.role), { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-950 px-4" data-testid="login-page">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(185,28,28,0.15),transparent_55%)]" />
      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-3">
            <div className="w-11 h-11 rounded-xl bg-red-700 flex items-center justify-center">
              <ShieldCheck className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-white tracking-tight">EMS <span className="text-red-600">Plateforme</span></span>
          </div>
          <p className="text-neutral-400 text-sm">Espace sécurisé — Gestion des interventions</p>
        </div>

        <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-8 shadow-2xl">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-neutral-300">Email</Label>
              <Input
                id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="vous@exemple.fr" required data-testid="login-email-input"
                className="mt-1.5 bg-neutral-800 border-neutral-700 text-white placeholder:text-neutral-500"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-neutral-300">Mot de passe</Label>
              <Input
                id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" required data-testid="login-password-input"
                className="mt-1.5 bg-neutral-800 border-neutral-700 text-white placeholder:text-neutral-500"
              />
            </div>
            {error && (
              <p className="text-sm text-red-400 bg-red-950/50 border border-red-900 rounded-lg px-3 py-2" data-testid="login-error">
                {error}
              </p>
            )}
            <Button type="submit" disabled={loading} data-testid="login-submit-button"
              className="w-full bg-red-700 hover:bg-red-600 text-white h-11">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
              Se connecter
            </Button>
          </form>
          <div className="mt-5 text-center">
            <Link to="/mot-de-passe-oublie" className="text-sm text-neutral-400 hover:text-red-500" data-testid="forgot-password-link">
              Mot de passe oublié ?
            </Link>
          </div>
        </div>
        <div className="text-center mt-6">
          <Link to="/" className="text-xs text-neutral-600 hover:text-neutral-400">← Retour au site EMS Express</Link>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
